import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import {
  Button,
  Heading,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@a7y/tailwind-catalyst";
import type { AuditTestRun } from "@a7y/types";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Clock } from "lucide-react";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  StatusBadge,
  PlatformBadge,
  PlatformIcon,
  formatDuration,
  formatRelativeTime,
} from "./shared";

export function RunGroupDetailPage() {
  const { runGroupId } = useParams<{ runGroupId: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery<{ data: AuditTestRun[]; total: number }>(
    {
      queryKey: ["audit-test-runs", "group", runGroupId],
      queryFn: () => api.getAuditTestRuns({ runGroupId, limit: 100 }),
      enabled: !!runGroupId,
      refetchInterval: (query) => {
        const runs = query.state.data?.data;
        const hasRunning = runs?.some((r) => r.status === "running");
        return hasRunning ? 5000 : 30000;
      },
    },
  );

  const runs = useMemo(() => data?.data ?? [], [data?.data]);

  // Derive summary from runs
  const summary = useMemo(() => {
    if (runs.length === 0) return null;
    const hasRunning = runs.some((r) => r.status === "running");
    const hasFailed = runs.some((r) => r.status === "failed");
    const status: "running" | "passed" | "failed" = hasRunning
      ? "running"
      : hasFailed
        ? "failed"
        : "passed";

    const triggerSource = runs[0].triggerSource;

    const earliestCreatedAt = runs.reduce(
      (earliest, r) => (r.createdAt < earliest ? r.createdAt : earliest),
      runs[0].createdAt,
    );

    const allCompleted = runs.every((r) => r.completedAt);
    const latestCompletedAt = allCompleted
      ? runs.reduce(
          (latest, r) =>
            r.completedAt && r.completedAt > (latest ?? "")
              ? r.completedAt
              : latest,
          null as string | null,
        )
      : null;

    const durationMs =
      latestCompletedAt && earliestCreatedAt
        ? new Date(latestCompletedAt).getTime() -
          new Date(earliestCreatedAt).getTime()
        : undefined;

    return {
      status,
      triggerSource,
      appCount: runs.length,
      createdAt: earliestCreatedAt,
      completedAt: latestCompletedAt,
      durationMs,
    };
  }, [runs]);

  // Sort runs: appName asc, then platform (appstore before playstore)
  const sortedRuns = useMemo(() => {
    return [...runs].sort((a, b) => {
      const nameCompare = a.appName.localeCompare(b.appName);
      if (nameCompare !== 0) return nameCompare;
      return a.platform.localeCompare(b.platform);
    });
  }, [runs]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-zinc-600"></div>
          <p className="text-gray-600 dark:text-zinc-400">
            Loading run group...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-0">
      <PageHeader
        title={`Run Group ${runGroupId?.slice(0, 8) ?? ""}`}
        subtitle="Audit test run group details"
      >
        <Button outline onClick={() => navigate("/admin/audit-testing")}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </PageHeader>

      {/* Summary Bar */}
      {summary && (
        <div className="mb-6 rounded-lg bg-white p-4 shadow-sm ring-1 ring-zinc-200 dark:bg-zinc-800 dark:ring-zinc-700">
          <div className="flex flex-wrap items-center gap-4">
            <StatusBadge status={summary.status} />
            <Text className="text-sm text-zinc-600 dark:text-zinc-400">
              {summary.appCount} app{summary.appCount !== 1 ? "s" : ""}
            </Text>
            <Text className="text-sm capitalize text-zinc-600 dark:text-zinc-400">
              {summary.triggerSource}
            </Text>
            <Text className="text-sm text-zinc-500">
              Started: {new Date(summary.createdAt).toLocaleString()}
            </Text>
            {summary.durationMs != null && (
              <div className="flex items-center gap-1 text-zinc-500">
                <Clock className="h-4 w-4" />
                <Text className="text-sm">
                  {formatDuration(summary.durationMs)}
                </Text>
              </div>
            )}
            {summary.status === "running" && (
              <div className="flex items-center gap-1 text-zinc-500">
                <Clock className="h-4 w-4" />
                <Text className="text-sm">In progress...</Text>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Runs Table */}
      <div className="rounded-lg bg-white shadow-sm ring-1 ring-zinc-200 dark:bg-zinc-800 dark:ring-zinc-700">
        <div className="border-b border-zinc-200 px-4 py-3 dark:border-zinc-700">
          <Heading level={2}>Runs</Heading>
        </div>
        <Table bleed>
          <TableHead>
            <TableRow>
              <TableHeader>App Name</TableHeader>
              <TableHeader>Platform</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>ASO Score</TableHeader>
              <TableHeader>Duration</TableHeader>
              <TableHeader>Date</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRuns.map((run) => {
              const runDurationMs =
                run.completedAt && run.createdAt
                  ? new Date(run.completedAt).getTime() -
                    new Date(run.createdAt).getTime()
                  : undefined;

              return (
                <TableRow
                  key={run.id}
                  className="cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-750"
                  onClick={() => navigate(`/admin/audit-testing/${run.id}`)}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <PlatformIcon platform={run.platform} />
                      <Text className="text-sm font-medium text-zinc-900 dark:text-white">
                        {run.appName}
                      </Text>
                    </div>
                  </TableCell>
                  <TableCell>
                    <PlatformBadge platform={run.platform} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={run.status} />
                  </TableCell>
                  <TableCell>
                    <Text className="text-sm text-zinc-600 dark:text-zinc-400">
                      {run.asoReadinessScore ?? "-"}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-zinc-500">
                      <Clock className="h-3 w-3" />
                      <Text className="text-sm">
                        {run.status === "running"
                          ? "In progress..."
                          : formatDuration(runDurationMs)}
                      </Text>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Text className="text-sm text-zinc-500">
                      {formatRelativeTime(run.createdAt)}
                    </Text>
                  </TableCell>
                </TableRow>
              );
            })}
            {sortedRuns.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <div className="flex items-center justify-center py-8">
                    <Text className="text-sm text-zinc-500">
                      No runs found for this group.
                    </Text>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
